import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { getEmbedUrl } from './videoUtils';

/**
 * VideoEmbed Node Extension
 * Renders YouTube and Vimeo videos as embedded iframes
 */
export interface VideoEmbedOptions {
  inline: boolean;
  allowFullscreen: boolean;
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    videoEmbed: {
      /**
       * Set a video embed
       */
      setVideoEmbed: (options: { src: string }) => ReturnType;
    };
  }
}

export const VideoEmbed = Node.create<VideoEmbedOptions>({
  name: 'videoEmbed',

  group: 'block',

  atom: true,

  draggable: true,

  addOptions() {
    return {
      inline: false,
      allowFullscreen: true,
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      src: {
        default: null,
        parseHTML: (element) => element.getAttribute('src'),
        renderHTML: (attributes) => {
          if (!attributes.src) {
            return {};
          }
          return {
            src: attributes.src,
          };
        },
      },
      type: {
        default: 'youtube',
        parseHTML: (element) => element.getAttribute('data-type'),
        renderHTML: (attributes) => {
          return {
            'data-type': attributes.type,
          };
        },
      },
      width: {
        default: '100%',
        parseHTML: (element) => element.getAttribute('width'),
        renderHTML: (attributes) => {
          return {
            width: attributes.width,
          };
        },
      },
      height: {
        default: '400',
        parseHTML: (element) => element.getAttribute('height'),
        renderHTML: (attributes) => {
          return {
            height: attributes.height,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-video-embed] iframe',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      {
        'data-video-embed': '',
        class: 'video-embed-wrapper',
      },
      [
        'iframe',
        mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
          frameborder: '0',
          allowfullscreen: this.options.allowFullscreen ? 'true' : 'false',
          allow:
            'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
          referrerpolicy: 'strict-origin-when-cross-origin',
          loading: 'lazy',
          title: `${HTMLAttributes.type || 'Video'} player`,
        }),
      ],
    ];
  },

  addCommands() {
    return {
      setVideoEmbed:
        (options) =>
        ({ commands }) => {
          const embedData = getEmbedUrl(options.src);

          if (!embedData) {
            return false;
          }

          return commands.insertContent({
            type: this.name,
            attrs: {
              src: embedData.embedUrl,
              type: embedData.type,
            },
          });
        },
    };
  },

  addPasteRules() {
    return [
      {
        find: /(https?:\/\/(?:www\.)?(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|vimeo\.com\/)(?:\S+))/gi,
        handler: ({ match, chain, range }) => {
          const url = match[1];
          const embedData = getEmbedUrl(url);

          if (!embedData) {
            return null;
          }

          // Delete the pasted URL text
          chain()
            .deleteRange(range)
            .insertContentAt(range.from, {
              type: this.name,
              attrs: {
                src: embedData.embedUrl,
                type: embedData.type,
              },
            })
            .run();
        },
      },
    ];
  },
});
